import { Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Column } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { InvoiceDetailEntity } from './invoice-detail.entity';
import { InvoiceEntity } from './invoice.entity';
import { OrderPromoApplicationEntity } from './order-promo-application.entity';
import { PromoEntity } from './promo.entity';
import { TermsCodeEntity } from './terms-code.entity';

/**
 * Why an application row instead of a column-soup approach:
 * - Same proven pattern as OrderPromoApplicationEntity — keeps order and
 *   invoice mental models aligned.
 * - Multi-source: order-derived promos, order-derived manual discount,
 *   invoice-only manual discount, and terms-code early-pay can all
 *   coexist on the same invoice without per-source columns proliferating.
 * - Queryable for "discount by source", joinable to promo / terms_code,
 *   indexable, and the aggregate (`invoice_detail.discount`) is just
 *   `SUM(discountAmount)` over the line.
 * - Returns/RMAs (not in this branch) read per-line discount amounts off
 *   these rows to compute partial-refund math.
 */
export enum InvoiceDiscountSource {
	/** Mirrored from an OrderPromoApplication when the invoice is created from a promo'd order. */
	ORDER_PROMO = 'order_promo',
	/** Distributed from `order.discount` (manual order header discount) at invoice creation. */
	ORDER_MANUAL = 'order_manual',
	/** Manual discount entered directly on the invoice by Admin/Accounting (header or per-line). */
	INVOICE_MANUAL = 'invoice_manual',
	/** Terms-code early-pay discount, applied at payment time when within the window. */
	TERMS_EARLY_PAY = 'terms_early_pay',
}

/**
 * Mirrors OrderPromoApplicationEntity's scope enum so the original
 * intent of the source discount is preserved when it flows from order
 * to invoice. Used for reporting ("how much SHIPPING-scope discount
 * did we give this quarter?") rather than computation.
 */
export type InvoiceDiscountScope = 'LINE' | 'ORDER' | 'SHIPPING';

/**
 * Per-line attribution of every discount applied to an invoice — for
 * reporting, payment-time discount calc, and (future) return/refund
 * proration. The aggregate per-line discount lives on
 * `invoice_detail.discount` and equals `SUM(discountAmount)` for that
 * line; the header `invoice.discount` is the total across all rows.
 *
 * Header-scoped sources (e.g. ORDER_MANUAL, INVOICE_MANUAL applied to
 * the whole invoice, TERMS_EARLY_PAY) are pre-distributed via
 * `prorateOrderLevelDiscount` (`libs/core/src/utils/promo-pricing/proration.ts`)
 * so every row points at a specific line (`fk_invoice_detail__id` set).
 * The only case where `fk_invoice_detail__id` is NULL is a
 * shipping-scope discount that doesn't attach to a line.
 */
@Index('idx_ida_invoice', ['fkInvoiceId'])
@Index('idx_ida_invoice_detail', ['fkInvoiceDetailId'])
@Index('idx_ida_source', ['discountSource', 'voidedDate'])
@Index('idx_ida_promo_active', ['fkPromoId', 'voidedDate'])
@Index('idx_ida_terms_code', ['fkTermsCodeId'])
@Entity('invoice_discount_application')
export class InvoiceDiscountApplicationEntity extends BaseEntity {
	@Column('int', { name: 'fk_invoice__id', unsigned: true })
	public fkInvoiceId!: number;

	/**
	 * Set on every row except SHIPPING-scope discounts that don't
	 * attach to a particular line. Pre-distribution writes one row per
	 * affected line.
	 */
	@Column('int', {
		name: 'fk_invoice_detail__id',
		nullable: true,
		unsigned: true,
	})
	public fkInvoiceDetailId!: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('varchar', { name: 'discountSource', length: 32 })
	public discountSource!: InvoiceDiscountSource;

	@Column('varchar', { name: 'appliedAtScope', length: 16 })
	public appliedAtScope!: InvoiceDiscountScope;

	/**
	 * The original discount type at the source (e.g. PERCENT, DOLLARS,
	 * FIXED_PRICE for promos; '$' or '%' for manual; PERCENT for
	 * terms-code early-pay). Stored for reporting, not used in
	 * computation — `discountAmount` is the canonical $ value.
	 */
	@Column('varchar', { name: 'discountTypeApplied', length: 32 })
	public discountTypeApplied!: string;

	/**
	 * The $ amount this line's price was reduced by from this discount
	 * source. (15,4) precision matches OrderPromoApplicationEntity so
	 * promo amounts flow through losslessly. Reports aggregate via
	 * `SUM(discountAmount) GROUP BY ...`.
	 */
	@Column('decimal', {
		name: 'discountAmount',
		precision: 15,
		scale: 4,
	})
	public discountAmount!: string;

	/**
	 * Vendor-funded cost reduction for this line via this discount
	 * (carried through from `order_promo_application.costAdjustment`).
	 * `0` for purely merchant-funded discounts. GP impact for this
	 * line equals `discountAmount - costAdjustment`.
	 */
	@Column('decimal', {
		name: 'costAdjustment',
		precision: 15,
		scale: 4,
		default: () => "'0'",
	})
	public costAdjustment!: string;

	/** Set for ORDER_PROMO rows. */
	@Column('int', { name: 'fk_promo__id', nullable: true, unsigned: true })
	public fkPromoId!: number | null;

	/**
	 * Set for ORDER_PROMO rows when this invoice was created from an
	 * order — points back at the originating order_promo_application
	 * for full traceability (e.g. for an end-to-end audit of "where did
	 * this $0.43 come from?").
	 */
	@Column('int', {
		name: 'fk_order_promo_application__id',
		nullable: true,
		unsigned: true,
	})
	public fkOrderPromoApplicationId!: number | null;

	/** Set for TERMS_EARLY_PAY rows. */
	@Column('int', {
		name: 'fk_terms_code__id',
		nullable: true,
		unsigned: true,
	})
	public fkTermsCodeId!: number | null;

	/**
	 * Free-form metadata: coupon code used (mirrored from
	 * OrderPromoApplicationEntity), operator note for INVOICE_MANUAL
	 * rows, etc.
	 */
	@Column('json', { name: 'sourceMetadata', nullable: true })
	public sourceMetadata!: Record<string, unknown> | null;

	@Column('datetime', { name: 'appliedDate' })
	public appliedDate!: Date;

	/**
	 * Set when the application is reversed (e.g. operator removes the
	 * INVOICE_MANUAL discount, terms early-pay is unwound, invoice is
	 * voided). Reports / aggregates exclude voided rows.
	 */
	@Column('datetime', { name: 'voidedDate', nullable: true })
	public voidedDate!: Date | null;

	@ManyToOne(() => InvoiceEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity;

	@ManyToOne(() => InvoiceDetailEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_invoice_detail__id', referencedColumnName: 'id' }])
	public fkInvoiceDetail!: InvoiceDetailEntity | null;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => PromoEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_promo__id', referencedColumnName: 'id' }])
	public fkPromo!: PromoEntity | null;

	@ManyToOne(() => OrderPromoApplicationEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([
		{
			name: 'fk_order_promo_application__id',
			referencedColumnName: 'id',
		},
	])
	public fkOrderPromoApplication!: OrderPromoApplicationEntity | null;

	@ManyToOne(() => TermsCodeEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_terms_code__id', referencedColumnName: 'id' }])
	public fkTermsCode!: TermsCodeEntity | null;
}
